import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { loadData } from '../services/codeDataStore';
import AJHMLogo from './AJHMLogo';
import { usePageContent } from '../services/pageContentService';
import HtmlContent from './HtmlContent';
import { 
  FaMapMarkerAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaLinkedin,
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaWhatsapp,
  FaTelegram,
  FaLink,
  FaShieldAlt,
  FaBookOpen
} from 'react-icons/fa';
import RawHtmlPage, { isRawHtmlPage } from './RawHtmlPage';

const Contact = () => {
  const { content: liveContact } = usePageContent('contact');
  const [contactInfo, setContactInfo] = useState(null);
  const [, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 140;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - (headerHeight + 20);
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  useEffect(() => {
    const fetchContact = async () => {
      setLoading(true);
      try {
        setContactInfo(await loadData('contact', null));
      } catch (err) {
        console.warn('Could not load contact info:', err);
      }
      setLoading(false);
    };
    fetchContact();
  }, []);

  const getSocialIcon = (link) => {
    const lower = link.toLowerCase();
    if (lower.includes('linkedin')) return <FaLinkedin className="text-base text-[#0A66C2]" />;
    if (lower.includes('facebook')) return <FaFacebook className="text-base text-[#1877F2]" />;
    if (lower.includes('instagram')) return <FaInstagram className="text-base text-[#E4405F]" />;
    if (lower.includes('youtube')) return <FaYoutube className="text-base text-[#CD201F]" />;
    if (lower.includes('whatsapp') || lower.includes('wa.me')) return <FaWhatsapp className="text-base text-[#25D366]" />;
    if (lower.includes('telegram')) return <FaTelegram className="text-base text-[#229ED9]" />;
    return <FaLink className="text-base text-gray-500" />;
  };

  if (isRawHtmlPage(liveContact)) {
    return <RawHtmlPage html={liveContact.full_content} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Contact</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        
        {/* Header Block matching the new design standard */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-2">
          <div>
            <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
              GET IN TOUCH
            </span>
            <h1 className="page-title">
              {liveContact?.titles?.page_title || 'Contact the Editorial Office'}
            </h1>
          </div>
        </div>

        {/* Optional Full Page Content if configured */}
        {liveContact?.full_content && (
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-100 mb-6">
            <div
              className="prose max-w-none text-gray-700 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: liveContact.full_content }}
            />
          </div>
        )}

        {/* Contact Cards */}
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6">
            
            {/* Primary Office Card */}
            <div className="bg-white rounded-3xl p-8 shadow-sm space-y-6">
              <div className="flex justify-center mb-2">
                <AJHMLogo />
              </div>

              <div className="space-y-4 pt-2">
                {/* 9.1 Journal Contact */}
                <div
                  id="journal-contact"
                  className="scroll-mt-36 sm:scroll-mt-40 border border-gray-200 rounded-2xl bg-white overflow-hidden shadow-xs"
                >
                  <div className="p-4 bg-[#FAFBFD] border-b border-gray-100">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Editorial Office</span>
                    <span className="font-bold text-base text-default">
                      {liveContact?.titles?.section_9_1 || 'Journal Contact'}
                    </span>
                  </div>

                  <div className="p-5 bg-white space-y-3.5">
                    <div className="flex items-start gap-3 p-3 bg-[#FAFBFD] rounded-xl border border-gray-100">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#03478E] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FaEnvelope className="text-sm" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                          Direct Email
                        </span>
                        <a 
                          href={`mailto:${liveContact?.journal_email?.split(',')[0]?.trim() || contactInfo?.email || 'editor@aimjhm.com'}`}
                          className="text-sm font-bold text-[#03478E] hover:underline block mt-0.5"
                        >
                          {liveContact?.journal_email || contactInfo?.email || 'editor@aimjhm.com'}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-[#FAFBFD] rounded-xl border border-gray-100">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#03478E] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FaPhoneAlt className="text-sm" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                          Editorial Hotline
                        </span>
                        <a 
                          href={`tel:${liveContact?.journal_phone?.split(',')[0]?.trim() || contactInfo?.phone || '+919412089404'}`}
                          className="text-sm font-bold text-gray-900 hover:text-[#03478E] block mt-0.5"
                        >
                          {liveContact?.journal_phone || (contactInfo?.phone ? `+91 ${contactInfo.phone}` : '+91 94120 89404')}
                        </a>
                        <span className="text-xs text-gray-500 block">
                          <HtmlContent inline content={liveContact?.office_hours} fallback="Mon – Sat, 9:00 AM – 5:00 PM IST" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 9.2 Publisher Contact */}
                <div
                  id="publisher-contact"
                  className="scroll-mt-36 sm:scroll-mt-40 border border-gray-200 rounded-2xl bg-white overflow-hidden shadow-xs"
                >
                  <div className="p-4 bg-[#FAFBFD] border-b border-gray-100">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Publishing House</span>
                    <span className="font-bold text-base text-default">
                      {liveContact?.titles?.section_9_2 || 'Publisher Contact & Office'}
                    </span>
                  </div>

                  <div className="p-5 bg-white space-y-3.5">
                    <div className="flex items-start gap-3 p-3 bg-[#FAFBFD] rounded-xl border border-gray-100">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#03478E] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <FaMapMarkerAlt className="text-sm" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                          Registered Office
                        </span>
                        <div className="text-sm font-semibold text-gray-800 mt-0.5 leading-snug">
                          <HtmlContent inline content={liveContact?.publisher_name} fallback="Domus Publications" />
                        </div>
                        <HtmlContent
                          content={liveContact?.publisher_address || contactInfo?.location}
                          fallback="Hill View Colony, Near Chandra Vatika, Khatima, Udham Singh Nagar - 262308, Uttarakhand, India"
                          className="text-xs text-gray-600 leading-relaxed mt-1"
                        />
                        {liveContact?.publisher_email && (
                          <p className="text-xs text-[#03478E] font-medium mt-1">
                            Email: {liveContact.publisher_email}
                          </p>
                        )}
                        {liveContact?.publisher_phone && (
                          <p className="text-xs text-gray-700 mt-0.5">
                            Phone: {liveContact.publisher_phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Social Channels if provided in Firestore */}
              {Array.isArray(contactInfo?.socialMedia) && contactInfo.socialMedia.filter(Boolean).length > 0 && (
                <div className="pt-4 border-t border-gray-100">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-3 text-center">
                    Official Communication Channels
                  </span>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {contactInfo.socialMedia.filter(Boolean).map((link, idx) => (
                      <a
                        key={idx}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-gray-50 hover:bg-blue-50 flex items-center justify-center transition shadow-2xs"
                      >
                        {getSocialIcon(link)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quick Links Card */}
            <div className="bg-white rounded-3xl p-6 shadow-sm space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                Quick Navigation
              </h4>
              <div className="space-y-2 text-xs font-semibold">
                <Link to="/information-for-authors" className="flex items-center justify-between p-2.5 rounded-xl hover:bg-blue-50 text-gray-700 hover:text-[#03478E] transition">
                  <span className="flex items-center gap-2">
                    <FaBookOpen className="text-gray-400" /> Author Guidelines
                  </span>
                  <span>&rarr;</span>
                </Link>
                <Link to="/submission-guidelines" className="flex items-center justify-between p-2.5 rounded-xl hover:bg-blue-50 text-gray-700 hover:text-[#03478E] transition">
                  <span className="flex items-center gap-2">
                    <FaShieldAlt className="text-gray-400" /> Peer Review Policy
                  </span>
                  <span>&rarr;</span>
                </Link>
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default Contact;